import { useState, useCallback, useMemo, useEffect, useRef } from 'react';

import { useCustomerConfig, useTpoGenConfig } from '../../../context/CustomerConfigContext';
import type {
    ScenarioPlan,
    CreateReportResult,
    CreateReportFormData,
    ComparisonReportDetailResponse,
    MetricType,
    PickerPlan,
} from '../../types';

interface UseScenarioComparisonStateArgs {
    initialBaseScenario?: ScenarioPlan | null;
    initialDim1Labels?: string[];
    initialDim2Labels?: string[];
    isNewReport?: boolean;
    reportDetail?: ComparisonReportDetailResponse | null;
}

const useScenarioComparisonState = ({
    initialBaseScenario,
    initialDim1Labels,
    initialDim2Labels,
    isNewReport = false,
    reportDetail,
}: UseScenarioComparisonStateArgs = {}) => {
    const tpoGenConfig = useTpoGenConfig();
    const { kpiCatalog } = useCustomerConfig();
    const { labels } = tpoGenConfig;
    const hasHydratedReport = useRef(false);

    const [baseScenario, setBaseScenario] = useState<ScenarioPlan | null>(() => initialBaseScenario ?? null);
    const [comparisonScenarios, setComparisonScenarios] = useState<ScenarioPlan[]>([]);
    const [selectedKpiKeys, setSelectedKpiKeys] = useState<string[]>(() => kpiCatalog.map((kpi) => kpi.key));
    const [metricType, setMetricType] = useState<MetricType>('abs');
    // A base scenario supplied up front means the user came from a plan rather than the
    // create-report modal, so that modal has nothing left to ask.
    const [showCreateReportModal, setShowCreateReportModal] = useState(() => isNewReport && !initialBaseScenario);
    const [showChangeBaseModal, setShowChangeBaseModal] = useState(false);
    const [showAddScenarioModal, setShowAddScenarioModal] = useState(false);
    // In that same case there is no typed name yet, so derive one from the plan.
    const [reportName, setReportName] = useState<string>(() =>
        initialBaseScenario?.planName
            ? labels.comparisonReport.defaultReportName(initialBaseScenario.planName)
            : labels.comparisonReport.scenarioComparison,
    );
    const [reportId, setReportId] = useState<string | null>(null);
    const [baseMissing, setBaseMissing] = useState(false);
    const [missingPlanIds, setMissingPlanIds] = useState<string[]>([]);
    const [reportFormData, setReportFormData] = useState<CreateReportFormData | null>(() => {
        if (initialDim1Labels?.length || initialDim2Labels?.length) {
            return {
                reportName: '',
                dim1_labels: initialDim1Labels ?? [],
                dim2_labels: initialDim2Labels ?? [],
                reportType: 'comparison',
            };
        }
        return null;
    });

    useEffect(() => {
        if (!reportDetail || hasHydratedReport.current) return;
        hasHydratedReport.current = true;

        const basePlan: ScenarioPlan = {
            planId: reportDetail.base_plan.plan_id,
            planName: reportDetail.base_plan.plan_name ?? '',
        };
        setBaseScenario(basePlan);

        const compPlans: ScenarioPlan[] = (reportDetail.comparison_plans ?? []).map((p) => ({
            planId: p.plan_id,
            planName: p.plan_name ?? '',
        }));
        setComparisonScenarios(compPlans);

        if (reportDetail.selection?.kpi_list) setSelectedKpiKeys(reportDetail.selection.kpi_list);
        if (reportDetail.selection?.metric_type) setMetricType(reportDetail.selection.metric_type);
        setReportName(reportDetail.name);
        setReportId(reportDetail.id);
        setBaseMissing(reportDetail.base_missing ?? false);
        setMissingPlanIds(reportDetail.missing_plan_ids ?? []);
        setReportFormData({
            reportName: reportDetail.name,
            dim1_labels: reportDetail.dim1_labels,
            dim2_labels: reportDetail.dim2_labels,
            reportType: 'comparison',
        });
    }, [reportDetail]);

    const allPlanIds = useMemo(() => {
        const ids: string[] = [];
        if (baseScenario) ids.push(baseScenario.planId);
        for (const s of comparisonScenarios) ids.push(s.planId);
        return ids;
    }, [baseScenario, comparisonScenarios]);

    const comparisonPlanIds = useMemo(
        () => comparisonScenarios.map((s) => s.planId).filter((id) => id !== baseScenario?.planId),
        [comparisonScenarios, baseScenario?.planId],
    );

    const handleCloseCreateReportModal = useCallback(() => setShowCreateReportModal(false), []);
    const handleOpenChangeBaseModal = useCallback(() => setShowChangeBaseModal(true), []);
    const handleCloseChangeBaseModal = useCallback(() => setShowChangeBaseModal(false), []);
    const handleOpenAddScenarioModal = useCallback(() => setShowAddScenarioModal(true), []);
    const handleCloseAddScenarioModal = useCallback(() => setShowAddScenarioModal(false), []);

    const handleCreateReport = useCallback(
        (result: CreateReportResult) => {
            const { formData, selectedPlan } = result;
            setBaseScenario(selectedPlan);
            setReportName(formData.reportName || labels.comparisonReport.scenarioComparison);
            setReportFormData(formData);
            setComparisonScenarios([]);
            setBaseMissing(false);
            setMissingPlanIds([]);
            setShowCreateReportModal(false);
        },
        [labels],
    );

    const handleReportSaved = useCallback((saved: ComparisonReportDetailResponse) => {
        hasHydratedReport.current = true;
        setReportId(saved.id);
        setReportName(saved.name);
    }, []);

    const handleChangeBase = useCallback((plan: PickerPlan) => {
        setComparisonScenarios((prev) => prev.filter((s) => s.planId !== plan.id));
        setBaseScenario({
            planId: plan.id,
            planName: plan.plan_name,
            createdBy: plan.created_by,
            year: plan.year,
        });
        setBaseMissing(false);
        setShowChangeBaseModal(false);
    }, []);

    const handleApplyScenarios = useCallback((selected: PickerPlan[]) => {
        const plans: ScenarioPlan[] = selected.map((p) => ({
            planId: p.id,
            planName: p.plan_name,
            createdBy: p.created_by,
            year: p.year,
        }));
        setComparisonScenarios((prev) => {
            const existingIds = new Set(prev.map((s) => s.planId));
            const newPlans = plans.filter((p) => !existingIds.has(p.planId));
            return [...prev, ...newPlans];
        });
        setShowAddScenarioModal(false);
    }, []);

    const handleRemoveScenario = useCallback((planId: string) => {
        setComparisonScenarios((prev) => prev.filter((s) => s.planId !== planId));
    }, []);

    const handleToggleMetricType = useCallback(() => {
        setMetricType((prev) => (prev === 'abs' ? 'percent' : 'abs'));
    }, []);

    return {
        baseScenario,
        comparisonScenarios,
        comparisonPlanIds,
        selectedKpiKeys,
        setSelectedKpiKeys,
        metricType,
        handleToggleMetricType,
        showCreateReportModal,
        showChangeBaseModal,
        showAddScenarioModal,
        reportName,
        setReportName,
        reportId,
        reportFormData,
        allPlanIds,
        baseMissing,
        missingPlanIds,
        handleCloseCreateReportModal,
        handleOpenChangeBaseModal,
        handleCloseChangeBaseModal,
        handleOpenAddScenarioModal,
        handleCloseAddScenarioModal,
        handleCreateReport,
        handleReportSaved,
        handleChangeBase,
        handleApplyScenarios,
        handleRemoveScenario,
    };
};

export default useScenarioComparisonState;
